/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Alert,
  AlertIcon,
  Text,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Flex,
  SimpleGrid,
  ButtonGroup,
  Link,
  FormControl,
  FormLabel,
  Badge,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link as RouterLink } from 'react-router-dom';
import 'mana-font/css/mana.css';
import '../../style/manaSymbols.css';

import CustomSelect from './CustomSelect';
import PaymentForm from '../PaymentForm';
import EventVariantItem from './EventVariantItem';
import EventCheckoutRecap from './EventCheckoutRecap';
import PuntiPaymentSummary from './PuntiPaymentSummary';
import {
  eventAllowsPuntiPayment,
  eventRequiresSoloPuntiPayment,
  eventAllowsPublicNonPuntiPayment,
} from '../../utils/eventPuntiPayment';
import { CIL_POINTS_LABEL } from '../../utils/cilPoints';
import {
  calculateVariantAvailableSeats,
  formatVariantSchedule,
  getVariantPrice,
  isEventEffectivelyFree,
} from '../../utils';
import { overlapWarning, preselectChoice } from './activityCheckout';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const EventDetailDrawer = ({
  isOpen,
  onModalClose,
  placement,
  event,
  userData,
  userDecks,
  paymentError,
  paymentLoading,
  paymentSuccess,
  paymentMethod,
  onPaymentSuccess,
  selectedParticipant,
  clientSecret,
  handleConfirm,
  getDisabledPartecipant,
  showPaymentForm,
  priceOption,
  setSelectedParticipant,
  setPaymentMethod,
  selectedVariantPriceOptions,
  handlePriceOptionChange,
  filteredVarianti,
  ordersFromOtherParticipants,
  onDeckSelectionChange,
  allVarianti = null,
  hasAlreadyPurchasedVariant = null,
  variantSelectionsConfirmed = false,
  onConfirmVariantSelections = null,
  onReopenVariantSelection = null,
  preselectedVariantId = null,
}) => {
  const [totalPrice, setTotalPrice] = useState(0);
  const [realTotalPrice, setRealTotalPrice] = useState(0);
  const [selectedDeck, setSelectedDeck] = useState('');
  const [participantPickerDismissed, setParticipantPickerDismissed] =
    useState(false);
  const preselectKeyRef = useRef('');

  const singleTesseramento = userData?.tesseramenti?.length === 1;

  const selectedTesseramentoRecord =
    userData?.tesseramenti?.find((t) => t.documentId === selectedParticipant) ||
    null;

  useEffect(() => {
    if (isOpen && !singleTesseramento) {
      setParticipantPickerDismissed(false);
    }
  }, [isOpen, singleTesseramento]);

  // Helper per verificare se un socio ha un tesseramento pending per l'anno corrente
  const hasPendingTesseramento = (socio) => {
    if (!socio || !socio.tesseramenti || !Array.isArray(socio.tesseramenti)) {
      return false;
    }

    const currentYear = new Date().getFullYear();

    return socio.tesseramenti.some(
      (t) => t.anno === currentYear && t.paymentStatus === 'pending'
    );
  };

  // Funzione per renderizzare i simboli di mana
  const renderManaSymbols = (colors) => {
    if (!colors) return null;

    const colorSymbols = colors.replace(/[{}]/g, '');
    return (
      <span style={{ fontFamily: 'Mana' }}>
        {colorSymbols.split('').map((color, index) => {
          const manaClass = {
            W: 'ms-w',
            U: 'ms-u',
            B: 'ms-b',
            R: 'ms-r',
            G: 'ms-g',
          }[color];

          if (!manaClass) return null;

          return (
            <span
              key={`${color}-${colorSymbols.substring(0, index + 1)}`}
              className={manaClass}
              style={{ fontSize: '1.2em', marginRight: '2px' }}
            />
          );
        })}
      </span>
    );
  };

  // Funzione per verificare se l'evento richiede selezione mazzo
  const checkIfDeckSelectionRequired = () => {
    if (!event?.checkoutMode) return false;
    return event.checkoutMode === 'deck';
  };

  // Verifica se c'e almeno uno sconto fratelli
  const atLeastOneDiscount = Object.values(selectedVariantPriceOptions).some(
    (variantOption) => variantOption.applyDiscount
  );

  const showMVPLabel =
    event.Sconto_MVP &&
    event?.Varianti?.length ===
      Object.keys(selectedVariantPriceOptions).length &&
    Object.values(selectedVariantPriceOptions).every(
      (variantOption) => variantOption.priceOption === 'totale'
    );

  const showScontoFratelliLabel =
    atLeastOneDiscount && event.Sconto_Fratelli && !showMVPLabel;

  // Solo acconti: tutte le varianti selezionate hanno priceOption === 'acconto'
  const isOnlyAcconti =
    Object.keys(selectedVariantPriceOptions).length > 0 &&
    Object.values(selectedVariantPriceOptions).every(
      (vo) => vo.priceOption === 'acconto'
    );

  // Se la regola MVP è soddisfatta (tutte le varianti selezionate in totale), permettere pagamento "altro" anche se onlinePaymentOnly è true
  const effectiveOnlinePaymentOnly = event?.onlinePaymentOnly && !showMVPLabel;
  const puntiCheckoutEnabled = eventAllowsPuntiPayment(event);
  // Con varianti la gratuità la decidono i prezzi delle varianti, non il flag is_free
  const eventIsFree = isEventEffectivelyFree(event);
  const soloPuntiOnly = eventRequiresSoloPuntiPayment(event) && !eventIsFree;
  const showCashAndCard = eventAllowsPublicNonPuntiPayment(event);

  // Tutte le varianti (future + disponibili) per la griglia: l’utente può selezionarne più di una
  const allVariantiForSelection =
    allVarianti?.length > 0 ? allVarianti : filteredVarianti || [];
  const hasVariantSelection =
    filteredVarianti?.length > 0 || allVariantiForSelection.length > 0;

  const variantConfirmFlowEnabled =
    typeof onConfirmVariantSelections === 'function';

  useEffect(() => {
    if (singleTesseramento && userData?.tesseramenti?.[0]?.documentId) {
      setSelectedParticipant(userData.tesseramenti[0].documentId);
    }
  }, [singleTesseramento, userData?.tesseramenti]);

  useEffect(() => {
    if (!isOpen) {
      preselectKeyRef.current = '';
      return;
    }
    if (!preselectedVariantId || !selectedParticipant) return;
    const key = `${preselectedVariantId}:${selectedParticipant}`;
    if (preselectKeyRef.current === key) return;

    const variante =
      (event?.Varianti || []).find(
        (item) => item.documentId === preselectedVariantId
      ) || null;
    if (!variante) return;
    preselectKeyRef.current = key;
    const seats = variante ? calculateVariantAvailableSeats(variante) : 0;
    const soldOut = Boolean(variante) && seats !== 999 && seats <= 0;
    const purchased = Boolean(
      hasAlreadyPurchasedVariant &&
        hasAlreadyPurchasedVariant(preselectedVariantId)
    );
    const applyDiscount =
      Boolean(ordersFromOtherParticipants?.[preselectedVariantId]) &&
      Number(event?.Sconto_Fratelli) > 0;
    const choice = preselectChoice({
      variante,
      purchased,
      soldOut,
      hasAcconto: Boolean(event?.accontoAmount),
      applyDiscount,
    });
    const current = selectedVariantPriceOptions[preselectedVariantId];
    if (!choice) {
      if (current) {
        handlePriceOptionChange({
          applyDiscount,
          variantId: preselectedVariantId,
          name: variante?.Nome || '',
          priceOption: '',
        });
      }
      return;
    }
    if (
      current?.priceOption === choice.priceOption &&
      Boolean(current.applyDiscount) === choice.applyDiscount
    ) {
      return;
    }
    handlePriceOptionChange(choice);
  }, [isOpen, preselectedVariantId, selectedParticipant]);

  // Auto-select first deck if user has only one deck and deck selection is required
  useEffect(() => {
    if (
      checkIfDeckSelectionRequired() &&
      userDecks &&
      userDecks.length === 1 &&
      !selectedDeck
    ) {
      const firstDeck = userDecks[0];
      const deckId = firstDeck.documentId || firstDeck.id;
      setSelectedDeck(deckId);
      if (onDeckSelectionChange) {
        onDeckSelectionChange(deckId);
      }
    }
  }, [userDecks, selectedDeck, onDeckSelectionChange]);

  // Auto-select payment method when solo un'opzione è disponibile
  useEffect(() => {
    const readyForPayment =
      selectedParticipant &&
      !eventIsFree &&
      !paymentMethod &&
      (!filteredVarianti?.length ||
        (filteredVarianti?.length > 0 &&
          Object.keys(selectedVariantPriceOptions).length > 0 &&
          totalPrice > 0 &&
          (!variantConfirmFlowEnabled || variantSelectionsConfirmed)));

    if (!readyForPayment) return;

    if (soloPuntiOnly && realTotalPrice > 0) {
      setPaymentMethod('punti');
      return;
    }
    if (effectiveOnlinePaymentOnly) {
      setPaymentMethod('elettronico');
    }
  }, [
    effectiveOnlinePaymentOnly,
    soloPuntiOnly,
    realTotalPrice,
    selectedParticipant,
    eventIsFree,
    paymentMethod,
    filteredVarianti,
    selectedVariantPriceOptions,
    totalPrice,
    variantConfirmFlowEnabled,
    variantSelectionsConfirmed,
  ]);

  useEffect(() => {
    if (filteredVarianti.length) {
      // Prezzo barrato = quanto pagheresti SENZA sconto fratelli (sconto solo sui totali, non sugli acconti)
      const realTotal = Object.keys(selectedVariantPriceOptions).reduce(
        (acc, variantId) => {
          const variantOption = selectedVariantPriceOptions[variantId];
          const variant = event.Varianti?.find(
            (v) => v.documentId === variantId
          );
          const variantPrice = getVariantPrice(variant, event);

          if (variantOption.priceOption === 'acconto') {
            return acc + (event.accontoAmount || 0);
          }
          return acc + variantPrice;
        },
        0
      );

      let total = Object.keys(selectedVariantPriceOptions).reduce(
        (acc, variantId) => {
          const variantOption = selectedVariantPriceOptions[variantId];
          const variant = event.Varianti?.find(
            (v) => v.documentId === variantId
          );
          const variantPrice = getVariantPrice(variant, event);

          if (variantOption.priceOption === 'acconto') {
            return acc + event.accontoAmount;
          }

          if (variantOption.applyDiscount) {
            const discountedAmount =
              variantPrice - (parseFloat(event.Sconto_Fratelli) || 0);
            return acc + discountedAmount;
          }

          return acc + variantPrice;
        },
        0
      );

      // Verifica se tutte le varianti sono state selezionate
      const allVariantsSelected =
        Object.keys(selectedVariantPriceOptions).length ===
        filteredVarianti.length;

      // Verifica se tutte le selezioni sono 'totale'
      const allTotale = Object.values(selectedVariantPriceOptions).every(
        (variantOption) => variantOption.priceOption === 'totale'
      );

      // Prezzo fisso quando si selezionano tutte le varianti (Sconto_MVP = prezzo in €)
      if (
        allVariantsSelected &&
        allTotale &&
        filteredVarianti.length === event.Varianti.length &&
        event.Sconto_MVP != null &&
        event.Sconto_MVP !== ''
      ) {
        total = Number(event.Sconto_MVP) || 0;
      }

      setTotalPrice(total);
      setRealTotalPrice(realTotal);
    } else {
      // Se non ci sono varianti selezionate, imposta al prezzo base
      setTotalPrice(event.prezzo);
      setRealTotalPrice(event.prezzo);
    }
  }, [
    filteredVarianti,
    selectedVariantPriceOptions,
    event.accontoAmount,
    event.prezzo,
    event.Varianti,
    event.Sconto_Fratelli,
    event.Sconto_MVP,
  ]);

  const nVariantSel = Object.keys(selectedVariantPriceOptions).length;
  const activityOverlapMessage = overlapWarning(
    Object.keys(selectedVariantPriceOptions)
      .map((variantId) =>
        (event.Varianti || []).find((item) => item.documentId === variantId)
      )
      .filter(Boolean)
  );
  const paidVariantTwoStep =
    variantConfirmFlowEnabled && nVariantSel > 0 && totalPrice > 0;
  const showVariantGrid = !paidVariantTwoStep || !variantSelectionsConfirmed;

  const autoOnlineCheckoutAfterVariantsRef = useRef(false);

  useEffect(() => {
    if (!variantSelectionsConfirmed) {
      autoOnlineCheckoutAfterVariantsRef.current = false;
    }
  }, [variantSelectionsConfirmed]);

  // Dopo conferma varianti, se solo pagamento online: crea PaymentIntent senza secondo click
  useEffect(() => {
    if (
      !paidVariantTwoStep ||
      !variantSelectionsConfirmed ||
      !effectiveOnlinePaymentOnly ||
      paymentMethod !== 'elettronico' ||
      showPaymentForm ||
      Boolean(clientSecret) ||
      paymentLoading ||
      autoOnlineCheckoutAfterVariantsRef.current
    ) {
      return;
    }
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && (!selectedDeck || !userDecks?.length)) {
      return;
    }
    autoOnlineCheckoutAfterVariantsRef.current = true;
    handleConfirm();
  }, [
    paidVariantTwoStep,
    variantSelectionsConfirmed,
    effectiveOnlinePaymentOnly,
    paymentMethod,
    showPaymentForm,
    clientSecret,
    paymentLoading,
    selectedDeck,
    userDecks,
    handleConfirm,
  ]);

  const hideVariantOnlineSecondStep =
    effectiveOnlinePaymentOnly &&
    paidVariantTwoStep &&
    variantSelectionsConfirmed;

  return (
    <Drawer
      isOpen={isOpen}
      placement={placement}
      onClose={onModalClose}
      size="md"
      portalProps={{ appendToParentPortal: false }}
    >
      <DrawerContent
        zIndex={100002}
        maxHeight={{ base: '90dvh', md: 'unset' }}
        width={{ base: '100%', md: '32rem' }}
        minW={{ base: '280px', md: '32rem' }}
        pointerEvents="auto"
      >
        <DrawerCloseButton top={4} right={4} />
        <DrawerHeader>{event.Nome}</DrawerHeader>
        <Divider />
        <DrawerBody paddingBottom={40} bg="white">
          <VStack align="start" mt={4} spacing={5}>
            {paymentSuccess ? (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Iscrizione avvenuta con successo 🚀
              </Alert>
            ) : (
              <>
                <EventCheckoutRecap event={event} />
                {!singleTesseramento && !participantPickerDismissed && (
                  <Box width="100%">
                    <Text fontSize="md" mb={3} fontWeight="bold">
                      Seleziona un partecipante:
                    </Text>
                    <SimpleGrid
                      columns={{ base: 1, md: 2 }}
                      spacing={4}
                      width="100%"
                    >
                      {userData.tesseramenti.map((tesseramento) => {
                        const isDisabled = getDisabledPartecipant(
                          tesseramento.documentId
                        );

                        const isSelected =
                          selectedParticipant === tesseramento.documentId;

                        const hasPending = hasPendingTesseramento(tesseramento);

                        return (
                          <Box
                            key={tesseramento.documentId}
                            p="8px 16px"
                            borderRadius="lg"
                            boxShadow="lg"
                            transition="transform 0.2s"
                            colorScheme="blue"
                            bg={isSelected ? 'blue.400' : 'white'}
                            color={isSelected ? 'white' : 'gray.500'}
                            borderColor={isSelected ? 'blue.400' : 'gray.200'}
                            borderWidth="2px"
                            _hover={
                              !isDisabled ? { transform: 'scale(1.03)' } : {}
                            }
                            cursor={isDisabled ? 'not-allowed' : 'pointer'}
                            opacity={isDisabled ? 0.6 : 1}
                            onClick={() => {
                              if (!isDisabled) {
                                setSelectedParticipant(tesseramento.documentId);
                              }
                            }}
                          >
                            <VStack align="flex-start" spacing={1}>
                              <Text fontSize="sm" fontWeight="bold">
                                {tesseramento.nomeSocio}
                                <br />
                                {tesseramento.cognomeSocio}
                              </Text>
                              {hasPending && (
                                <Badge
                                  colorScheme="orange"
                                  fontSize="xs"
                                  borderRadius="sm"
                                  mt={1}
                                >
                                  Tesseramento incompleto
                                </Badge>
                              )}
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                )}
                {!singleTesseramento &&
                  userData.tesseramenti.some((socio) =>
                    hasPendingTesseramento(socio)
                  ) && (
                    <Alert status="warning" borderRadius="md" mt={0}>
                      <AlertIcon />
                      <Text fontSize="sm">
                        <strong>Importante:</strong> Se il tesseramento non
                        risulta completato al momento della data di
                        partecipazione, la prenotazione si considera annullata.{' '}
                        <RouterLink
                          to="/account/tesseramenti"
                          style={{
                            color: '#3182ce',
                            textDecoration: 'underline',
                          }}
                        >
                          Completa il tesseramento
                        </RouterLink>
                      </Text>
                    </Alert>
                  )}

                {/* Campo selezione mazzo per eventi specifici */}
                {checkIfDeckSelectionRequired() && selectedParticipant && (
                  <Box width="100%">
                    <Divider mb={4} />

                    {/* Header sezione mazzo */}
                    <Box mb={4}>
                      <FormLabel
                        fontSize="lg"
                        fontWeight="bold"
                        mb={2}
                        color="purple.700"
                      >
                        🃏 Seleziona il tuo mazzo
                        {userDecks && userDecks.length > 1
                          ? ` (${userDecks.length} disponibili)`
                          : ''}
                      </FormLabel>

                      {/* Spiegazione */}
                      <Box
                        p={4}
                        bg="purple.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="purple.200"
                        mb={4}
                      >
                        <Text
                          fontSize="sm"
                          color="purple.800"
                          fontWeight="medium"
                          mb={2}
                        >
                          📋 Perché devo selezionare un mazzo?
                        </Text>
                        <Text fontSize="sm" color="purple.700" lineHeight="1.6">
                          Questo evento richiede la selezione di un mazzo per
                          garantire un&apos;esperienza di gioco ottimale e
                          bilanciata. Il mazzo selezionato verrà utilizzato per
                          l&apos;evento e non potrà essere modificato dopo la
                          conferma.
                        </Text>
                      </Box>
                    </Box>

                    <FormControl isRequired>
                      <CustomSelect
                        value={selectedDeck}
                        onChange={(value) => {
                          setSelectedDeck(value);
                          if (onDeckSelectionChange) {
                            onDeckSelectionChange(value);
                          }
                        }}
                        options={
                          userDecks && userDecks.length > 0
                            ? [
                                {
                                  value: '',
                                  label: 'Nessun mazzo selezionato',
                                },
                                ...userDecks.map((deck) => ({
                                  value: deck.documentId || deck.id,
                                  label: deck.nomeMazzo,
                                  colors: deck.colors,
                                })),
                              ]
                            : [
                                {
                                  value: '',
                                  label: 'Nessun mazzo disponibile',
                                  disabled: true,
                                },
                              ]
                        }
                        placeholder="Seleziona un mazzo"
                        size="lg"
                        colorScheme="purple"
                        variant="outline"
                        isDisabled={!userDecks || userDecks.length === 0}
                        renderOption={(option) => (
                          <HStack spacing={2}>
                            {option.colors && renderManaSymbols(option.colors)}
                            <Text>{option.label}</Text>
                          </HStack>
                        )}
                        renderValue={(option) => (
                          <HStack spacing={2}>
                            {option.colors && renderManaSymbols(option.colors)}
                            <Text>{option.label}</Text>
                          </HStack>
                        )}
                      />

                      {/* Messaggi informativi */}
                      {(!userDecks || userDecks.length === 0) && (
                        <Box mt={4}>
                          <Text fontSize="sm" color="gray.600" mb={3}>
                            Non hai ancora creato nessun mazzo per questo
                            evento.
                          </Text>

                          {/* Box con opzioni per creare/importare mazzi */}
                          <Box
                            p={4}
                            bg="blue.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="blue.200"
                          >
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="blue.800"
                              mb={3}
                            >
                              🚀 Crea il tuo primo mazzo
                            </Text>

                            <Box textAlign="center" mt={3}>
                              <Text fontSize="sm" color="blue.700" mb={3}>
                                Crea il tuo mazzo da zero o importalo da
                                Moxfield, MTGO, Arena e altre piattaforme
                              </Text>
                              <Button
                                onClick={() => {
                                  window.location.href = '/decks';
                                }}
                                leftIcon={
                                  <span
                                    role="img"
                                    aria-label="Crea/Importa mazzo"
                                  >
                                    🚀
                                  </span>
                                }
                                colorScheme="blue"
                                variant="solid"
                                size="md"
                                fontWeight="medium"
                                _hover={{ bg: 'blue.600' }}
                              >
                                Crea/Importa Mazzo
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {userDecks && userDecks.length > 0 && (
                        <Text
                          fontSize="sm"
                          color="green.600"
                          mt={3}
                          fontWeight="medium"
                        >
                          ✓ Hai {userDecks.length} mazzo
                          {userDecks.length !== 1 ? 'i' : ''} disponibile
                          {userDecks.length !== 1 ? 'i' : ''}
                        </Text>
                      )}

                      {selectedDeck && userDecks && userDecks.length > 0 && (
                        <Box
                          mt={3}
                          p={3}
                          bg="green.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="green.200"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Text
                              fontSize="sm"
                              color="green.800"
                              fontWeight="medium"
                            >
                              ✓ Mazzo selezionato:
                            </Text>
                            {(() => {
                              const selectedDeckData = userDecks.find(
                                (d) => (d.documentId || d.id) === selectedDeck
                              );
                              return (
                                <Box display="flex" alignItems="center" gap={1}>
                                  {renderManaSymbols(selectedDeckData?.colors)}
                                  <Text
                                    fontSize="sm"
                                    color="green.800"
                                    fontWeight="medium"
                                  >
                                    {selectedDeckData?.nomeMazzo}
                                  </Text>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      )}

                      {checkIfDeckSelectionRequired() &&
                        userDecks &&
                        userDecks.length > 0 &&
                        !selectedDeck && (
                          <Box
                            mt={3}
                            p={3}
                            bg="orange.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="orange.200"
                          >
                            <Text
                              fontSize="sm"
                              color="orange.800"
                              fontWeight="medium"
                            >
                              ⚠️ Seleziona un mazzo per continuare
                            </Text>
                          </Box>
                        )}

                      {/* Bottone per gestire i mazzi */}
                      {userDecks && userDecks.length > 0 && (
                        <Box mt={3} textAlign="center">
                          <Button
                            onClick={() => {
                              window.location.href = '/decks';
                            }}
                            leftIcon={
                              <span role="img" aria-label="Gestisci mazzi">
                                🎴
                              </span>
                            }
                            colorScheme="blue"
                            variant="outline"
                            size="md"
                            fontWeight="medium"
                            _hover={{ bg: 'blue.50', color: 'blue.800' }}
                          >
                            Gestisci i tuoi mazzi
                          </Button>
                        </Box>
                      )}
                    </FormControl>
                  </Box>
                )}
                {!filteredVarianti?.length &&
                  selectedParticipant &&
                  !event.is_free && (
                    <>
                      {!singleTesseramento && <Divider />}
                      {!singleTesseramento &&
                        participantPickerDismissed &&
                        selectedTesseramentoRecord && (
                          <Box mb={4} p={3} bg="gray.50" borderRadius="md">
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              fontWeight="medium"
                              mb={1}
                            >
                              Partecipante
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold">
                              {selectedTesseramentoRecord.nomeSocio}{' '}
                              {selectedTesseramentoRecord.cognomeSocio}
                            </Text>
                            {hasPendingTesseramento(
                              selectedTesseramentoRecord
                            ) && (
                              <Badge
                                colorScheme="orange"
                                fontSize="xs"
                                borderRadius="sm"
                                mt={2}
                              >
                                Tesseramento incompleto
                              </Badge>
                            )}
                          </Box>
                        )}
                      {Boolean(totalPrice) && (
                        <Text
                          fontSize="2xl"
                          color="brand.300"
                          textAlign="right"
                          width="100%"
                        >
                          Totale: <b>€{totalPrice}</b>
                        </Text>
                      )}
                      <Divider />

                      {effectiveOnlinePaymentOnly ? (
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          Per questo evento è disponibile solo il pagamento
                          online
                        </Alert>
                      ) : soloPuntiOnly && realTotalPrice > 0 ? (
                        <>
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            Per questo evento è disponibile solo il pagamento
                            con {CIL_POINTS_LABEL}
                          </Alert>
                          {paymentMethod === 'punti' && (
                            <PuntiPaymentSummary
                              saldo={userData?.punti}
                              costo={realTotalPrice}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {showCashAndCard && (
                            <Box
                              p={4}
                              bg="blue.50"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="blue.200"
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color="blue.800"
                                mb={2}
                              >
                                💳 Pagamento Elettronico Raccomandato
                              </Text>
                              <Text
                                fontSize="sm"
                                color="blue.700"
                                lineHeight="1.6"
                              >
                                Ti consigliamo di utilizzare il pagamento
                                elettronico per questo evento. Questo ci
                                permette un maggior controllo sulle iscrizioni e
                                una amministrazione più trasparente e smart. Il
                                pagamento elettronico garantisce una conferma
                                immediata della tua partecipazione.
                              </Text>
                            </Box>
                          )}

                          <Text fontSize="md" mb="-8px" fontWeight="bold">
                            Seleziona il metodo di pagamento:
                          </Text>
                          <ButtonGroup spacing={4}>
                            {showCashAndCard && priceOption !== 'saldo' && (
                              <Button
                                onClick={() => setPaymentMethod('contanti')}
                                variant={
                                  paymentMethod === 'contanti'
                                    ? 'solid'
                                    : 'outline'
                                }
                                bg={
                                  paymentMethod === 'contanti'
                                    ? 'blue.400'
                                    : 'transparent'
                                }
                                color={
                                  paymentMethod === 'contanti'
                                    ? 'white'
                                    : 'grey.500'
                                }
                              >
                                💰 Altro
                              </Button>
                            )}
                            {showCashAndCard && (
                              <Button
                                onClick={() => {
                                  setPaymentMethod('elettronico');
                                }}
                                variant={
                                  paymentMethod === 'elettronico'
                                    ? 'solid'
                                    : 'outline'
                                }
                                isLoading={paymentLoading}
                                disabled={paymentLoading}
                                bg={
                                  paymentMethod === 'elettronico'
                                    ? 'blue.400'
                                    : 'transparent'
                                }
                                color={
                                  paymentMethod === 'elettronico'
                                    ? 'white'
                                    : 'grey.500'
                                }
                              >
                                💳 Elettronico
                              </Button>
                            )}
                            {puntiCheckoutEnabled && realTotalPrice > 0 && (
                              <Button
                                onClick={() => setPaymentMethod('punti')}
                                variant={
                                  paymentMethod === 'punti'
                                    ? 'solid'
                                    : 'outline'
                                }
                                bg={
                                  paymentMethod === 'punti'
                                    ? 'blue.400'
                                    : 'transparent'
                                }
                                color={
                                  paymentMethod === 'punti'
                                    ? 'white'
                                    : 'grey.500'
                                }
                              >
                                ⭐ {CIL_POINTS_LABEL}
                              </Button>
                            )}
                          </ButtonGroup>
                          {paymentMethod === 'punti' && (
                            <PuntiPaymentSummary
                              saldo={userData?.punti}
                              costo={realTotalPrice}
                            />
                          )}
                        </>
                      )}
                      <Divider />

                      {/* Contanti => crea ordine con status pending */}
                      {paymentMethod === 'contanti' && (
                        <Center
                          w="100%"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          <Text mb="4" textAlign="center">
                            {' '}
                            Puoi trovare maggiori informazioni sui metodi di
                            pagamento disponibili visitando la pagina{' '}
                            <Link color="blue.600" href="/account/ordini">
                              Ordini.
                            </Link>
                            <br />
                            <br /> Premendo il pulsante PRENOTA confermi la tua
                            partecipazione all&apos;evento.{' '}
                          </Text>
                          <Button
                            width="200px"
                            colorScheme="green"
                            onClick={handleConfirm}
                            disabled={
                              paymentLoading ||
                              (checkIfDeckSelectionRequired() &&
                                (!selectedDeck || !userDecks?.length))
                            }
                            isLoading={paymentLoading}
                          >
                            Prenota
                          </Button>
                        </Center>
                      )}
                      {paymentMethod === 'punti' && (
                        <Center
                          w="100%"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          <Text
                            mb="4"
                            textAlign="center"
                            fontSize="sm"
                            color="gray.600"
                          >
                            Conferma per scalare i {CIL_POINTS_LABEL} e
                            completare l&apos;iscrizione.
                          </Text>
                          <Button
                            width="200px"
                            colorScheme="green"
                            onClick={handleConfirm}
                            disabled={
                              paymentLoading ||
                              (Number(userData?.punti) || 0) <
                                (realTotalPrice || 0) ||
                              (checkIfDeckSelectionRequired() &&
                                (!selectedDeck || !userDecks?.length))
                            }
                            isLoading={paymentLoading}
                          >
                            Paga con {CIL_POINTS_LABEL}
                          </Button>
                        </Center>
                      )}
                      {paymentMethod === 'elettronico' && (
                        <Center
                          w="100%"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          <Text mb="4" textAlign="center">
                            Premendo il pulsante <b>Procedi al pagamento</b>{' '}
                            verrà avviato il checkout elettronico sicuro.
                          </Text>
                          <Button
                            width="240px"
                            colorScheme="blue"
                            onClick={() => {
                              setParticipantPickerDismissed(true);
                              handleConfirm();
                            }}
                            disabled={
                              paymentLoading ||
                              (checkIfDeckSelectionRequired() &&
                                (!selectedDeck || !userDecks?.length))
                            }
                            isLoading={paymentLoading}
                            data-testid="event-electronic-proceed-button"
                          >
                            Procedi al pagamento
                          </Button>
                        </Center>
                      )}
                    </>
                  )}
                {/* Evento con varianti */}
                {hasVariantSelection && selectedParticipant ? (
                  <Box width="100%">
                    <>
                      {!singleTesseramento && <Divider mb={4} />}
                      {showVariantGrid && (
                        <>
                          <Text fontSize="md" mb={4} fontWeight="bold">
                            {allVariantiForSelection.length > 1
                              ? 'Scegli una o più attività'
                              : 'Attività'}
                          </Text>
                          <SimpleGrid
                            mb={4}
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                          >
                            {allVariantiForSelection.map((variante) => {
                              const isSelected =
                                selectedVariantPriceOptions[variante.documentId]
                                  ?.priceOption;

                              const applyDiscount =
                                Boolean(
                                  ordersFromOtherParticipants[
                                    variante.documentId
                                  ]
                                ) && Number(event.Sconto_Fratelli) > 0;

                              const isAlreadyPurchased =
                                hasAlreadyPurchasedVariant &&
                                hasAlreadyPurchasedVariant(variante.documentId);

                              return (
                                <EventVariantItem
                                  key={variante.documentId}
                                  applyDiscount={applyDiscount}
                                  variante={variante}
                                  event={event}
                                  isSelected={isSelected}
                                  selectedVariantPriceOptions={
                                    selectedVariantPriceOptions
                                  }
                                  handlePriceOptionChange={
                                    handlePriceOptionChange
                                  }
                                  isAlreadyPurchased={isAlreadyPurchased}
                                />
                              );
                            })}
                          </SimpleGrid>
                        </>
                      )}
                      {paidVariantTwoStep && variantSelectionsConfirmed && (
                        <Box
                          mb={4}
                          p={4}
                          bg="white"
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor="gray.200"
                        >
                          <Text fontSize="md" fontWeight="bold" mb={3}>
                            La tua iscrizione
                          </Text>
                          {!singleTesseramento &&
                            selectedTesseramentoRecord && (
                              <Box
                                mb={3}
                                pb={3}
                                borderBottomWidth="1px"
                                borderColor="gray.200"
                              >
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="medium"
                                  mb={1}
                                >
                                  Partecipante
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold">
                                  {selectedTesseramentoRecord.nomeSocio}{' '}
                                  {selectedTesseramentoRecord.cognomeSocio}
                                </Text>
                                {hasPendingTesseramento(
                                  selectedTesseramentoRecord
                                ) && (
                                  <Badge
                                    colorScheme="orange"
                                    fontSize="xs"
                                    borderRadius="sm"
                                    mt={2}
                                  >
                                    Tesseramento incompleto
                                  </Badge>
                                )}
                              </Box>
                            )}
                          <VStack align="stretch" spacing={3}>
                            {Object.entries(selectedVariantPriceOptions).map(
                              ([variantId, variant]) => {
                                const variante = (event.Varianti || []).find(
                                  (item) => item.documentId === variantId
                                );
                                const schedule = variante
                                  ? formatVariantSchedule(variante)
                                  : { date: null, time: null };
                                const when = [schedule.date, schedule.time]
                                  .filter(Boolean)
                                  .join(' · ');
                                let priceLabel = '';
                                if (variant.priceOption === 'acconto') {
                                  priceLabel = `Acconto €${event.accontoAmount || 0}`;
                                } else if (variante) {
                                  const price = getVariantPrice(
                                    variante,
                                    event
                                  );
                                  const amount = variant.applyDiscount
                                    ? price -
                                      (parseFloat(event.Sconto_Fratelli) || 0)
                                    : price;
                                  priceLabel =
                                    amount === 0 || amount == null
                                      ? 'Gratis'
                                      : `€${amount}`;
                                }
                                return (
                                  <Flex
                                    key={`sum-${variantId}`}
                                    justify="space-between"
                                    align="flex-start"
                                    gap={3}
                                  >
                                    <Box minW={0}>
                                      <Text fontSize="sm" fontWeight="semibold">
                                        {variante?.Nome || variant.name}
                                      </Text>
                                      {when ? (
                                        <Text fontSize="xs" color="gray.600">
                                          {when}
                                        </Text>
                                      ) : null}
                                    </Box>
                                    {priceLabel ? (
                                      <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        flexShrink={0}
                                      >
                                        {priceLabel}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                );
                              }
                            )}
                          </VStack>
                          {onReopenVariantSelection && (
                            <Flex justify="flex-start" width="100%" mt={3}>
                              <Button
                                size="sm"
                                variant="link"
                                colorScheme="blue"
                                onClick={() => {
                                  setParticipantPickerDismissed(false);
                                  onReopenVariantSelection();
                                }}
                              >
                                Modifica selezione
                              </Button>
                            </Flex>
                          )}
                        </Box>
                      )}
                      {activityOverlapMessage ? (
                        <Alert status="warning" borderRadius="md" mb={4}>
                          <AlertIcon />
                          <Text fontSize="sm">{activityOverlapMessage}</Text>
                        </Alert>
                      ) : null}
                      {nVariantSel > 0 && Boolean(totalPrice) && (
                        <Flex
                          justify="space-between"
                          align="baseline"
                          width="100%"
                          mb={2}
                          mt={2}
                        >
                          <Text fontSize="md" color="gray.700">
                            Totale
                          </Text>
                          <Box
                            fontSize="2xl"
                            color="brand.300"
                            textAlign="right"
                          >
                            {(showScontoFratelliLabel || showMVPLabel) &&
                              !isOnlyAcconti && (
                                <span>
                                  <s>€{realTotalPrice}</s>
                                  &nbsp;
                                </span>
                              )}
                            <b>€{totalPrice}</b>
                            {showScontoFratelliLabel && !showMVPLabel && (
                              <Text fontSize="md" color="green.500">
                                {isOnlyAcconti
                                  ? 'Lo sconto viene applicato al saldo'
                                  : 'Sconto Fratelli Attivato'}
                              </Text>
                            )}
                            {showMVPLabel && !showScontoFratelliLabel && (
                              <Text fontSize="md" color="green.500">
                                Sconto MVP Attivato
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      )}
                      {nVariantSel > 0 && totalPrice === 0 ? (
                        <Center w="100%" flexDirection="column">
                          <Text mb="4" textAlign="center">
                            Premendo il pulsante <b>Partecipa</b> confermi la
                            tua partecipazione all&apos;evento.
                          </Text>
                          <Button
                            mt={4}
                            width="200px"
                            colorScheme="green"
                            onClick={() => handleConfirm({ isFree: true })}
                            isDisabled={
                              !selectedParticipant ||
                              paymentLoading ||
                              (checkIfDeckSelectionRequired() &&
                                (!selectedDeck || !userDecks?.length))
                            }
                            isLoading={paymentLoading}
                          >
                            Partecipa
                          </Button>
                        </Center>
                      ) : (
                        nVariantSel > 0 &&
                        totalPrice > 0 && (
                          <>
                            {paidVariantTwoStep &&
                              !variantSelectionsConfirmed &&
                              onConfirmVariantSelections && (
                                <Center w="100%" flexDirection="column" mb={6}>
                                  <Button
                                    width="100%"
                                    colorScheme="blue"
                                    onClick={() => {
                                      setParticipantPickerDismissed(true);
                                      onConfirmVariantSelections();
                                    }}
                                    isDisabled={
                                      paymentLoading ||
                                      (checkIfDeckSelectionRequired() &&
                                        (!selectedDeck || !userDecks?.length))
                                    }
                                  >
                                    Procedi al pagamento
                                  </Button>
                                </Center>
                              )}
                            {(!paidVariantTwoStep ||
                              variantSelectionsConfirmed) && (
                              <>
                                {!hideVariantOnlineSecondStep && (
                                  <>
                                    <Divider mb={4} />

                                    {effectiveOnlinePaymentOnly ? (
                                      <Alert
                                        status="info"
                                        borderRadius="md"
                                        mb={4}
                                      >
                                        <AlertIcon />
                                        Per questo evento è disponibile solo il
                                        pagamento online
                                      </Alert>
                                    ) : soloPuntiOnly && realTotalPrice > 0 ? (
                                      <>
                                        <Alert
                                          status="info"
                                          borderRadius="md"
                                          mb={4}
                                        >
                                          <AlertIcon />
                                          Per questo evento è disponibile solo
                                          il pagamento con {CIL_POINTS_LABEL}
                                        </Alert>
                                        {paymentMethod === 'punti' && (
                                          <PuntiPaymentSummary
                                            saldo={userData?.punti}
                                            costo={realTotalPrice}
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {showCashAndCard && (
                                          <Box
                                            p={4}
                                            bg="blue.50"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="blue.200"
                                            mb={4}
                                          >
                                            <Text
                                              fontSize="sm"
                                              fontWeight="semibold"
                                              color="blue.800"
                                              mb={2}
                                            >
                                              💳 Pagamento Elettronico
                                              Raccomandato
                                            </Text>
                                            <Text
                                              fontSize="sm"
                                              color="blue.700"
                                              lineHeight="1.6"
                                            >
                                              Ti consigliamo di utilizzare il
                                              pagamento elettronico per questo
                                              evento. Questo ci permette un
                                              maggior controllo sulle iscrizioni
                                              e una amministrazione più
                                              trasparente e smart. Il pagamento
                                              elettronico garantisce una
                                              conferma immediata della tua
                                              partecipazione.
                                            </Text>
                                          </Box>
                                        )}

                                        <Text
                                          fontSize="md"
                                          mb={4}
                                          fontWeight="bold"
                                        >
                                          Seleziona il metodo di pagamento:
                                        </Text>
                                        <ButtonGroup spacing={4}>
                                          {showCashAndCard &&
                                            priceOption !== 'saldo' && (
                                              <Button
                                                onClick={() =>
                                                  setPaymentMethod('contanti')
                                                }
                                                variant={
                                                  paymentMethod === 'contanti'
                                                    ? 'solid'
                                                    : 'outline'
                                                }
                                                colorScheme="blue"
                                              >
                                                💰 Altro
                                              </Button>
                                            )}
                                          {showCashAndCard && (
                                            <Button
                                              onClick={() => {
                                                setPaymentMethod('elettronico');
                                              }}
                                              variant={
                                                paymentMethod === 'elettronico'
                                                  ? 'solid'
                                                  : 'outline'
                                              }
                                              isLoading={paymentLoading}
                                              colorScheme="blue"
                                            >
                                              💳 Elettronico
                                            </Button>
                                          )}
                                          {puntiCheckoutEnabled &&
                                            realTotalPrice > 0 && (
                                              <Button
                                                onClick={() =>
                                                  setPaymentMethod('punti')
                                                }
                                                variant={
                                                  paymentMethod === 'punti'
                                                    ? 'solid'
                                                    : 'outline'
                                                }
                                                colorScheme="blue"
                                              >
                                                ⭐ {CIL_POINTS_LABEL}
                                              </Button>
                                            )}
                                        </ButtonGroup>
                                        {paymentMethod === 'punti' && (
                                          <PuntiPaymentSummary
                                            saldo={userData?.punti}
                                            costo={realTotalPrice}
                                          />
                                        )}
                                      </>
                                    )}
                                    <Divider mt={8} mb={6} />
                                  </>
                                )}

                                {hideVariantOnlineSecondStep &&
                                  paymentLoading &&
                                  !clientSecret &&
                                  !showPaymentForm && (
                                    <Center py={4} mb={2}>
                                      <Text fontSize="sm" color="gray.600">
                                        Preparazione del pagamento...
                                      </Text>
                                    </Center>
                                  )}

                                {!hideVariantOnlineSecondStep &&
                                  paymentMethod === 'punti' && (
                                    <Center
                                      w="100%"
                                      justifyContent="center"
                                      flexDirection="column"
                                    >
                                      <Text
                                        mb="6"
                                        fontSize="sm"
                                        textAlign="center"
                                        color="gray.600"
                                      >
                                        Conferma per scalare i{' '}
                                        {CIL_POINTS_LABEL} e completare
                                        l&apos;iscrizione.
                                      </Text>
                                      <Button
                                        width="200px"
                                        colorScheme="green"
                                        onClick={handleConfirm}
                                        disabled={
                                          paymentLoading ||
                                          (Number(userData?.punti) || 0) <
                                            (realTotalPrice || 0) ||
                                          (checkIfDeckSelectionRequired() &&
                                            (!selectedDeck ||
                                              !userDecks?.length))
                                        }
                                        isLoading={paymentLoading}
                                        data-testid="event-payment-punti-button"
                                      >
                                        Paga con {CIL_POINTS_LABEL}
                                      </Button>
                                    </Center>
                                  )}

                                {!hideVariantOnlineSecondStep &&
                                  paymentMethod === 'contanti' && (
                                    <Center
                                      w="100%"
                                      justifyContent="center"
                                      flexDirection="column"
                                    >
                                      <Text
                                        mb="6"
                                        fontSize="sm"
                                        textAlign="center"
                                      >
                                        Premendo il pulsante qui sotto confermi
                                        la tua partecipazione all&apos;evento.
                                      </Text>
                                      <Button
                                        width="200px"
                                        colorScheme="green"
                                        onClick={handleConfirm}
                                        disabled={
                                          checkIfDeckSelectionRequired() &&
                                          (!selectedDeck || !userDecks?.length)
                                        }
                                        data-testid="event-payment-button"
                                      >
                                        Prenota
                                      </Button>
                                      <Text
                                        mt="3"
                                        fontSize="sm"
                                        textAlign="center"
                                        color="gray.500"
                                      >
                                        Puoi trovare maggiori informazioni sui
                                        metodi di pagamento disponibili
                                        visitando la pagina{' '}
                                        <Link
                                          color="blue.600"
                                          href="/account/ordini"
                                        >
                                          Ordini.
                                        </Link>
                                      </Text>
                                    </Center>
                                  )}
                                {paymentMethod === 'elettronico' &&
                                  !showPaymentForm &&
                                  !hideVariantOnlineSecondStep && (
                                    <Center
                                      w="100%"
                                      justifyContent="center"
                                      flexDirection="column"
                                    >
                                      <Text mb="4" textAlign="center">
                                        Premendo il pulsante{' '}
                                        <b>Procedi al pagamento</b> verrà
                                        avviato il checkout elettronico sicuro.
                                      </Text>
                                      <Button
                                        width="240px"
                                        colorScheme="blue"
                                        onClick={() => handleConfirm()}
                                        disabled={
                                          paymentLoading ||
                                          (checkIfDeckSelectionRequired() &&
                                            (!selectedDeck ||
                                              !userDecks?.length))
                                        }
                                        isLoading={paymentLoading}
                                        data-testid="variant-electronic-proceed-button"
                                      >
                                        Procedi al pagamento
                                      </Button>
                                    </Center>
                                  )}
                              </>
                            )}
                          </>
                        )
                      )}
                    </>
                  </Box>
                ) : (
                  !hasVariantSelection &&
                  event.is_free && (
                    <Center w="100%" flexDirection="column">
                      <Text mb="4" textAlign="center">
                        Premendo il pulsante <b>Partecipa</b> confermi la tua
                        partecipazione all&apos;evento.
                      </Text>
                      <Button
                        mt={4}
                        width="200px"
                        colorScheme="green"
                        onClick={() => handleConfirm({ isFree: true })}
                        isDisabled={
                          !selectedParticipant ||
                          paymentLoading ||
                          (checkIfDeckSelectionRequired() &&
                            (!selectedDeck || !userDecks?.length))
                        }
                        isLoading={paymentLoading}
                      >
                        Partecipa
                      </Button>
                    </Center>
                  )
                )}
                {/* PaymentForm se elettronico e PaymentIntent c'è */}
                {showPaymentForm && paymentMethod === 'elettronico' && (
                  <Box width="100%" mt="0">
                    {clientSecret && !paymentLoading ? (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                        }}
                      >
                        <PaymentForm onSuccess={onPaymentSuccess} />
                      </Elements>
                    ) : (
                      !paymentLoading && (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          {paymentError ||
                            'Errore nella creazione del pagamento'}
                        </Alert>
                      )
                    )}
                  </Box>
                )}
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default EventDetailDrawer;
